// The install-time stamp seed primitive. Deliberately knows NOTHING about the install
// flow: it turns the RELEASE MANIFEST (what this install deploys) into an InstalledStamp.
// The installer calls it through `m8t platform seed-stamp`.
//
// IT USED TO SEED FROM THE BAKED DESCRIPTOR, AND THAT WAS A BUG BY CONSTRUCTION.
// Every subsequent converge stamps from the manifest (see platform-converge.ts), so a
// from-zero install seeded from a different document could hand the updater a row
// describing something other than what the release says — and the very first update
// would diff against it. Now both come from one source, and "the seed agrees with what
// a converge would write" stops being a property anyone has to maintain: it is the
// same values read out of the same file.
import { platformTag } from "@m8t-stack/platform-release";
import type { InstalledStamp, ImageStampEntry, PersonaStampEntry } from "@m8t-stack/platform-release";
import type { ReleaseManifest } from "./release-manifest.js";
import { seedSetRev } from "./platform-converge.js";

export const IMAGE_COMPONENTS = ["gateway", "codingAgent", "azureExecutor"] as const;
export type ImageComponentKey = (typeof IMAGE_COMPONENTS)[number];

const BARE_SEMVER = /^\d+\.\d+\.\d+(?:-canary\.(?:0|[1-9]\d*))?$/;

/**
 * Is this a bare X.Y.Z release version?
 *
 * `platformTag()` normalizes prefixes but validates nothing, so without this a
 * manifest declaring "latest" would stamp `platform-vlatest` just as happily as
 * a release.
 */
export function isBarePlatformVersion(v: unknown): v is string {
  return typeof v === "string" && BARE_SEMVER.test(v);
}

/**
 * Build the stamp a fresh install should carry, from the manifest that install
 * deployed. Version forms are load-bearing: `platformVersion` is TAG form
 * (platform-vX.Y.Z), component tags stay v-prefixed exactly as the converge
 * engine writes them.
 *
 * `previousPlatformVersion` is null — a seeded install has no prior release, so
 * there is no rollback target to claim. `foundryVersion` is "" because the seed
 * does not read back a deployed prompt version (converge's noop branch writes ""
 * in the same case).
 *
 * There is NO version override parameter any more. The engine has already proven
 * the manifest declares the release it was told to install (manifest_declares_version
 * in installer/lib.sh), so a second value here could only ever disagree with a
 * fact already established — and a stamp is exactly the wrong place to discover
 * a disagreement, since it is what the updater diffs against and what fleet
 * telemetry aggregates.
 */
export function buildSeedStamp(m: ReleaseManifest, cliVersion: string, nowIso: string): InstalledStamp {
  if (!isBarePlatformVersion(m.platform.version)) {
    throw new Error(`manifest platform.version must be a bare X.Y.Z release version, got ${JSON.stringify(m.platform.version)}`);
  }
  const img = (k: ImageComponentKey): ImageStampEntry => ({
    tag: m.components[k].tag,
    digest: m.components[k].digest,
    state: "managed",
  });
  const personas: Record<string, PersonaStampEntry> = {};
  for (const [name, item] of Object.entries(m.components.personas.items)) {
    personas[name] = { treeSha: item.treeSha, foundryVersion: "" };
  }
  const stamp: InstalledStamp = {
    schemaVersion: 1,
    platformVersion: platformTag(m.platform.version),
    previousPlatformVersion: null,
    updatedAt: nowIso,
    lastResult: "success",
    cli: cliVersion,
    origin: "install-seed",
    components: {
      gateway: img("gateway"),
      codingAgent: img("codingAgent"),
      azureExecutor: img("azureExecutor"),
      personas,
      infra: { treeSha: m.components.infra?.treeSha ?? "" },
    },
  };
  // Computed with the converge engine's own function, not re-derived here. Two
  // implementations of this string already have to agree byte for byte
  // (install-descriptor-gen writes it, platform-converge computes it); a third
  // would be the duplication this whole change exists to remove.
  const seedRev = seedSetRev(m);
  if (seedRev) stamp.components.brainSeeds = { seedRev };
  return stamp;
}
